/**
 * The two shapes an account's colour takes outside the accounts screen.
 *
 * One file rather than four copies of `<span class="swatch" style=...>`,
 * because the rule that matters here is not the markup -- it is that the colour
 * never appears without the account's name beside it. A dot on its own is
 * meaningless to a reader who cannot distinguish two of the eight, and silent
 * to one who is listening to the page rather than looking at it. Keeping both
 * shapes here means the rule is enforced by which component you reach for: the
 * chip carries the name itself, and `AccountDot` is `aria-hidden` and is only
 * ever placed against text that already names the account.
 *
 * The style sets a custom property to a `var()` reference rather than to a
 * colour. The stylesheet holds a light and a dark value for each token and the
 * theme toggle swaps between them; an inline style naming a resolved colour
 * would be frozen on whichever theme was current when the element was drawn.
 */

import { colourVar } from "../lib/palette";
import * as store from "../lib/store";

/**
 * A coloured dot. Decorative by construction -- place it beside text that says
 * which account this is, never instead of it.
 */
export function AccountDot({ accountId }: { accountId: string }) {
  const colour = store.colourOf(accountId);
  if (!colour) return null;
  return (
    <span
      class="swatch"
      aria-hidden="true"
      style={{ "--acct-colour": colourVar(colour) } as Record<string, string>}
    />
  );
}

/** The dot and the account's name, as one unit that wraps together. */
export function AccountChip({ accountId, name }: { accountId: string; name: string }) {
  return (
    <span class="acct-chip">
      <AccountDot accountId={accountId} />
      {name}
    </span>
  );
}
